// src/pages/guesttable.js
import React, { useState, useEffect, useCallback } from 'react';
import { useMemo } from 'react'; 
import supabase from '../config/supabaseClient';
import '../components/guesttable.css';
import { v4 as uuidv4 } from 'uuid';
import MessageIcon from '../icons/messageicon';
import RemoveFriendIcon from '../icons/removefriendicon';
import GuestSettingsIcon from '../icons/guestsettingsicon';
import SendInviteIcon from '../icons/sendinviteicon';
import CloseIcon from '../icons/closeicon';
import DoneIcon from '../icons/doneicon';
import ProfileCapsule from '../pages/profilecapsule'; 
import LogoMark from '../assets/s2slogo.png';
import { usePassport } from '../passport';



console.log('🚀 GuestTable.js is loaded');

export default function GuestTable({ eventId, eventTitle, vrc, onClose, viewerRole, user, profile }) {

  console.log("👀 GuestTable re-rendered");
  const [guests, setGuests] = useState([]);
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState('');
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [editedPassphrase, setEditedPassphrase] = useState('');
  const [activeView, setActiveView] = useState('table'); // or 'profile'
  const [notification, setNotification] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [nonMemberName, setNonMemberName] = useState('')
  const GENERIC_AVATAR = 'https://api.dicebear.com/6.x/bottts/svg?seed=guest';
  const evtId = eventId;


  const loadMembers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, user_id, first_name, last_name, email');

    if (error) {
      console.error('❌ Error loading members:', error);
    } else {
      console.log('✅ Loaded members:', data.length);
      setMembers(data || []);
    }
  };

  useEffect(() => {
    console.log('🧪 useEffect: calling loadMembers');
    loadMembers();
  }, []);


  const filteredSuggestions = useMemo(() => {
    if (query.length < 2) return [];
    const lower = query.toLowerCase();
    return members.filter(
      (m) =>
        m.first_name?.toLowerCase().includes(lower) ||
        m.last_name?.toLowerCase().includes(lower)
    );
  }, [query, members]);


    const addGuest = async (member) => {
    console.log('👤 Adding guest:', member);
    console.log("🔥 addGuest called for:", member);

    const alreadyAdded = guests.some(g => g.guest_id === member.user_id);
if (alreadyAdded) {
  setNotification(`${member.first_name} is already in the guest list.`);
  setTimeout(() => setNotification(''), 3000);
  return;
}


            if (!member.user_id) {
          console.warn('⚠️ Cannot add guest without a user_id:', member);
          setNotification('Only registered users can be added as guests.');
          setTimeout(() => setNotification(''), 3000);
          return;
        }


    const newEntry = {
      event_id: evtId,
      guest_first_name: member.first_name,
      guest_last_name: member.last_name,
      guest_id: member.user_id || null,  // ✅ Must be the Supabase Auth ID (RSVP)
      invited_by: user?.id || null,             // ✅ Track who invited them
      vrc,
      passphrase: ' ',
      status: 'Invite Not Sent',                // ✅ Better default status
      guest_qr_token: uuidv4(),
    };

    try {
      const { error } = await supabase
        .from('guests')
        .insert([newEntry], { returning: 'representation' })
        .single();
      if (error) throw error;
      await loadGuests(); // ✅ re-fetch guest list
      setQuery('');
      setNotification('Guest added successfully!');
    } catch (err) {
      console.error('Error saving guest:', err);
      setNotification('Error saving guest');
    } finally {
      setTimeout(() => setNotification(''), 3000);
    }
  };

    const addNonMember = async () => {
    const typed = (nonMemberName || '').trim();
    if (!typed) return;

    // Split "First Last..." (anything after first space goes to last name)
    const parts = typed.split(/\s+/);
    const first = parts[0] || '';
    const last = parts.slice(1).join(' ') || '—';

    const newEntry = {
      event_id: evtId,
      guest_first_name: first,
      guest_last_name: last,
      guest_id: null,              // ← non-member: no Supabase auth user
      invited_by: user?.id || null,
      vrc,
      passphrase: ' ',             // keep parity with your member flow
      status: 'Invite Not Sent',   // same default as members
      guest_qr_token: uuidv4(),
    };

    try {
      const { error } = await supabase.from('guests').insert([newEntry]).single();
      if (error) throw error;

      await loadGuests();
      setNonMemberName('');
      setNotification('Guest added successfully!');
    } catch (err) {
      console.error('Error saving non-member:', err);
      setNotification('Error saving guest');
    } finally {
      setTimeout(() => setNotification(''), 3000);
    }
  };


  const suggestionList = useMemo(() => {
    if (filteredSuggestions.length === 0) return null;
    return (
      <ul className="friends-suggestions">
        {filteredSuggestions.map((m) => (
          <li key={m.id} onClick={() => addGuest(m)}>
            {m.first_name} {m.last_name} ({m.email})
          </li>
        ))}
      </ul>
    );
  }, [filteredSuggestions, addGuest]);

  
    useEffect(() => {
  if (evtId) loadGuests();
}, [evtId]);


  const loadGuests = async () => {
    console.log("✅ loadGuests called");

  if (!evtId) return;
  try {
    // 1. Get guest rows for this event
    const { data: guestData, error: guestError } = await supabase
      .from('guests')
      .select('id, guest_id, guest_first_name, guest_last_name, vrc, passphrase, status, invited_by, guest_comments')
      .eq('event_id', evtId);

    if (guestError) throw guestError;

    // 2. Gather all unique inviter and guest user_ids
    const inviterIds = [...new Set(guestData.map(g => g.invited_by).filter(Boolean))];
    const guestIds = [...new Set(guestData.map(g => g.guest_id).filter(Boolean))];

    // 3. Fetch inviter profile names
    const { data: inviters } = await supabase
      .from('profiles')
      .select('user_id, first_name, last_name')
      .in('user_id', inviterIds);

    const inviterMap = {};
    inviters?.forEach(i => {
      inviterMap[i.user_id] = `${i.first_name} ${i.last_name}`;
    });

    // 4. Fetch guest avatars/photos
    const { data: guestProfiles } = await supabase
      .from('profiles')
      .select('user_id, avatar_or_pic, user_photo, user_avatar')
      .in('user_id', guestIds);

    const profileMap = {};
    guestProfiles?.forEach(p => {
      profileMap[p.user_id] = {
        avatar_or_pic: p.avatar_or_pic,
        user_photo: p.user_photo,
        user_avatar: p.user_avatar,
      };
    });

    // 5. Merge all info into guest objects
    const guestsWithProfiles = guestData.map(g => {
      const profile = profileMap[g.guest_id] || {};
      return {
        ...g,
        invited_by_name: inviterMap[g.invited_by] || '—',
        guest_comments: g.guest_comments || '',
        ...profile
      };
    });

    setGuests(guestsWithProfiles);
    console.log("🎯 Setting guests with", guestData.length, "rows");
  } catch (err) {
    console.error('Error loading guests:', err);
    setNotification('Error loading guests');
    setTimeout(() => setNotification(''), 3000);
  }
}; 

    // Can return early
    if (!user || !profile) {
      console.warn('🚨 GuestTable loaded without an authenticated user!');
      return null;
    }

  if (!['host', 'attendee'].includes(viewerRole)) return null;


  return (
  <div className="guesttable-overlay" onClick={onClose}>
    <div className={`guesttable-popup ${activeView === 'profile' ? 'profile-view-mode' : ''}`} onClick={(e) => e.stopPropagation()}>
      {activeView === 'profile' && viewingProfile ? (
        <>
          <button
            className="close-btn"
            onClick={() => {
              setViewingProfile(null);
              setActiveView('table');
            }}
            style={{ marginBottom: '12px' }}
          >
            ← Back to Guest List
          </button>

          <div className="capsule-wrapper">
            <ProfileCapsule profile={viewingProfile} />
          </div>
        </>
      ) : (
        <>
        
      <h2 className="guesttable-title">Guests for: {eventTitle}</h2>
      {notification && <div className="success-message">{notification}</div>}

      <div className="add-rows">
        {/* Left: add/search a MEMBER (existing behavior) */}
        <div className="add-col" style={{ position: 'relative' }}>
          <input
            type="text"
            className="search-input"
            placeholder="Add a member…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (!query.trim()) return;

                const typed = query.trim();
                const exactMatch = members.find(
                  (m) =>
                    `${m.first_name} ${m.last_name}`.toLowerCase() ===
                    typed.toLowerCase()
                );

                if (exactMatch) {
                  addGuest(exactMatch);
                } else {
                  setNotification('This guest must be a registered user.');
                  setTimeout(() => setNotification(''), 3000);
                }
              }
            }}
          />
          {suggestionList /* keep suggestions ONLY for member search */}
        </div>

        {/* Middle: your logo */}
        <img src={LogoMark} alt="logo" className="add-divider-logo" />

        {/* Right: quick-add a NON-MEMBER */}
        <div className="add-col">
          <input
            type="text"
            className="search-input"
            placeholder="Add a non-member…"
            value={nonMemberName}
            onChange={(e) => setNonMemberName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNonMember();
              }
            }}
          />
        </div>
      </div>


      <table className="guesttable-table">
        <thead>
          <tr>
            <th>Guest</th>
            <th>VRC</th>
            {viewerRole === 'host' && <th>Passphrase</th>}
            <th>Invited By</th>
            <th>Status</th>
            <th>Response</th>
            {viewerRole === 'host' && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {guests
            .filter((g) => g && g.guest_first_name != null)
            .map((g) => (
              <tr key={g.id}>
                <td>
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {setViewingProfile(g);
                      setActiveView('profile');
                    }}

                  >
                  <div className="guest-name-cell">
                    <img
                      src={
                        g.guest_id
                          ? (g.avatar_or_pic === 'photo'
                              ? g.user_photo
                              : g.user_avatar || `https://api.dicebear.com/6.x/bottts/svg?seed=${g.guest_id}`)
                          : GENERIC_AVATAR
                      }
                      alt={`${g.guest_first_name}'s avatar`}
                      className="guest-avatar"
                    />
                    <div className="guest-name-text">
                      {g.guest_first_name} {g.guest_last_name}
                    </div>
                  </div>
                </div>
                </td>

                <td>{g.vrc}</td>

                {viewerRole === 'host' && (
                  <td>
                    {editingGuestId === g.id ? (
                      <input
                        type="text"
                        value={editedPassphrase}
                        onChange={(e) => setEditedPassphrase(e.target.value)}
                        className="passphrase-input"
                      />
                    ) : (
                      g.passphrase
                    )}
                  </td>
                )}

                <td>{g.invited_by_name}</td>
                <td>{g.status}</td>
                <td>{g.guest_comments || '—'}</td>

                {viewerRole === 'host' && (
                  <td className="actions-cell">
                    {editingGuestId === g.id ? (
                      <div className="action-buttons">
                        <button
                          title="Save"
                          onClick={async () => {
                            const { error } = await supabase
                              .from('guests')
                              .update({ passphrase: editedPassphrase })
                              .eq('id', g.id);
                            if (error) {
                              console.error('Update failed:', error);
                              setNotification('Failed to update passphrase');
                            } else {
                              setGuests((prev) =>
                                prev.map((guest) =>
                                  guest.id === g.id
                                    ? {
                                        ...guest,
                                        passphrase: editedPassphrase,
                                      }
                                    : guest
                                )
                              );
                              setNotification('Passphrase updated!');
                            }
                            setEditingGuestId(null);
                            setEditedPassphrase('');
                            setTimeout(() => setNotification(''), 3000);
                          }}
                        >
                          <DoneIcon size={24} color="#28a745" />
                        </button>

                        <button
                          title="Cancel"
                          onClick={() => {
                            setEditingGuestId(null);
                            setEditedPassphrase('');
                          }}
                        >
                          <CloseIcon size={24} color="#FF5722" />
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button
                          title="Message Guest"
                          onClick={() =>
                            /*alert(`Message ${g.guest_first_name}`)*/
                            alert('Feature coming soon!')
                          }
                        >
                          <MessageIcon size={24} color="#333" />
                        </button>
                        <button
                          title="Send Invite"
                          onClick={async () => {
                            if (['Invited', 'Pending', 'Going', "Already Invited"].includes(g.status)) {
                              alert(`You've already sent an invitation to ${g.guest_first_name}`);
                              return;
                            }

                            const confirmSend = window.confirm(`Send invitation to ${g.guest_first_name} ${g.guest_last_name}?`);
                            if (!confirmSend) return;

                            try {
                              const { error } = await supabase
                                .from('guests')
                                .update({ status: 'Pending', invited_by: user.id })
                                .eq('id', g.id);

                              if (error) throw error;

                              setGuests((prev) =>
                                prev.map((guest) =>
                                  guest.id === g.id ? { ...guest, status: 'Pending', invited_by_name: profile.first_name + ' ' + profile.last_name } : guest
                                )
                              );

                              setNotification(`✅ Invitation sent to ${g.guest_first_name}`);
                            } catch (err) {
                              console.error('Error sending invite:', err);
                              setNotification('❌ Failed to send invite');
                            } finally {
                              setTimeout(() => setNotification(''), 3000);
                            }
                          }}
                        >
                          <SendInviteIcon size={24} color="#333" />
                        </button>

                        <button
                          title="Edit Guest"
                          onClick={() => {
                            setEditingGuestId(g.id);
                            setEditedPassphrase(g.passphrase || '');
                          }}
                        >
                          <GuestSettingsIcon size={24} color="#333" />
                        </button>
                        <button
                          title="Remove Guest"
                          onClick={async () => {
                            if (
                              !window.confirm(`Remove ${g.guest_first_name}?`)
                            )
                              return;
                            try {
                              await supabase
                                .from('guests')
                                .delete()
                                .eq('id', g.id);
                              setGuests((prev) =>
                                prev.filter((guest) => guest.id !== g.id)
                              );
                            } catch (err) {
                              console.error('Delete failed:', err);
                              setNotification('Error deleting guest');
                              setTimeout(
                                () => setNotification(''),
                                3000
                              );
                            }
                          }}
                        >
                          <RemoveFriendIcon size={24} color="#c00" />
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </table>

      <button
        className="close-button-icon"
        onClick={onClose}
        title="Close"
      >
        <CloseIcon size={24} color="white" />
      </button>
      </>
      )}
    </div>
  </div>
);
}